import { Switch } from '@/components/Controls/Switch';
import { ImageUpload } from '@/components/FileUpload/ImageUpload';
import { FormManagementProps } from '@/type/form.types';
import { ErrorResponse } from '@/utils/axios.http';
import { UseMutateFunction } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { FieldValues, Path, PathValue } from 'react-hook-form';
import { R2_PUBLIC_DOMAIN } from '@/utils/constant.utils';

interface GroupCheckPointLicensePlateProps<
  TSchema extends FieldValues,
> extends FormManagementProps<TSchema> {
  blurCheckPointLicensePlateField: string;
  checkPointLicensePlateImageField: string;
  blurCheckPointLicensePlateValue: boolean;
  checkPointLicensePlateImageValue: string | null;
  mutateUpdateGroup?: UseMutateFunction<
    AxiosResponse<TSchema, unknown>,
    ErrorResponse,
    TSchema,
    unknown
  >;
  mutateUploadImage?: UseMutateFunction<
    AxiosResponse<unknown, unknown>,
    ErrorResponse,
    FormData,
    unknown
  >;
}

export const GroupCheckPointLicensePlate = <TSchema extends FieldValues>({
  t,
  blurCheckPointLicensePlateField,
  checkPointLicensePlateImageField,
  blurCheckPointLicensePlateValue,
  checkPointLicensePlateImageValue,
  data,
  register,
  setValue,
  submitDisabled,
  mutateUpdateGroup,
  mutateUploadImage,
}: GroupCheckPointLicensePlateProps<TSchema>) => {
  return (
    <div>
      <div className="flex flex-wrap gap-6 max-sm:gap-3">
        <Switch
          containerClassName="max-sm:w-full"
          leftText={t('common:blur')}
          {...(register &&
            register(blurCheckPointLicensePlateField as Path<TSchema>))}
          disabled={submitDisabled}
          checked={!!blurCheckPointLicensePlateValue}
          onChange={(e) => {
            const checked = e.target.checked;

            setValue(
              blurCheckPointLicensePlateField as Path<TSchema>,
              checked as PathValue<TSchema, Path<TSchema>>,
            );

            setValue(checkPointLicensePlateImageField as Path<TSchema>, null);

            mutateUpdateGroup({
              ...data,
              groupCustomImage: {
                ...data?.groupCustomImage,
                [blurCheckPointLicensePlateField]: checked,
                [checkPointLicensePlateImageField]: null,
              },
            });
          }}
        />
        <div className="font-medium text-gray-300 max-sm:hidden">|</div>
        <Switch
          containerClassName="max-sm:w-full"
          leftText={`${t('common:replaceWithImage')} (PNG)`}
          {...(register &&
            register(checkPointLicensePlateImageField as Path<TSchema>))}
          disabled={submitDisabled}
          checked={!!checkPointLicensePlateImageValue}
          onChange={(e) => {
            const checked = e.target.checked;

            setValue(
              checkPointLicensePlateImageField as Path<TSchema>,
              checked as PathValue<TSchema, Path<TSchema>>,
            );

            if (checked && blurCheckPointLicensePlateValue) {
              setValue(
                blurCheckPointLicensePlateField as Path<TSchema>,
                false as PathValue<TSchema, Path<TSchema>>,
              );

              mutateUpdateGroup({
                ...data,
                groupCustomImage: {
                  ...data?.groupCustomImage,
                  [blurCheckPointLicensePlateField]: false,
                },
              });
            } else if (checkPointLicensePlateImageValue?.includes('.')) {
              mutateUpdateGroup({
                ...data,
                groupCustomImage: {
                  ...data?.groupCustomImage,
                  [checkPointLicensePlateImageField]: null,
                },
              });
            }
          }}
        />
      </div>
      {checkPointLicensePlateImageValue && (
        <div className="mt-5">
          <ImageUpload
            t={t}
            chooseAnotherImgContainerClassName="mt-6"
            allowedFormats={['png']}
            disabledChooseAnotherImgButton={submitDisabled}
            mutateUploadImage={mutateUploadImage}
            initialImage={
              data?.groupCustomImage?.[checkPointLicensePlateImageField] &&
              `${R2_PUBLIC_DOMAIN}/images/group/${data?.id}/${data?.groupCustomImage?.[checkPointLicensePlateImageField]}`
            }
          />
        </div>
      )}
    </div>
  );
};
